import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = {
  key: 0,
  class: "c-featured-content-cards__header"
}
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { class: "c-featured-content-cards__content" }
const _hoisted_5 = ["href"]
const _hoisted_6 = {
  key: 1,
  class: "c-featured-content-cards__pagination",
  "aria-hidden": "true"
}
const _hoisted_7 = ["onClick"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  FeaturedContentCardsItemFields,
  FeaturedContentCardsProps,
} from '@/types/components/FeaturedContentCards';
import AdaptiveImage from '@/elements/AdaptiveImage.vue';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedCard extends ScItem<FeaturedContentCardsItemFields> {
  fields: FeaturedContentCardsItemFields;
  label: string;
  labelStyle: Record<string, string>;
  href: string;
  target?: string;
  linkText: string;
  linkLabel: string;
  showLink: boolean;
  hasMedia: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FeaturedContentCards',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const fields = computed(() => props.fields);
const mobileTrackEl = ref<HTMLElement | null>(null);
const activeCardIndex = ref(0);
const titleHeight = ref(0);
let titleResizeObserver: ResizeObserver | null = null;

const rootClass = computed(() =>
  utils.overall.baseComClass(props.fields, 'c-featured-content-cards')
);
const rootStyle = computed(() => ({
  ...(utils.overall?.baseComStyle?.(fields.value) || {}),
  '--featured-card-title-height': titleHeight.value ? `${titleHeight.value}px` : 'auto',
}));

const rawCards = computed<ScItem<FeaturedContentCardsItemFields>[]>(() => {
  const cardItems = utils.sc.getFieldObj(fields.value, 'cards');
  return Array.isArray(cardItems) ? cardItems : [];
});

const cards = computed<NormalizedCard[]>(() =>
  rawCards.value
    .map((card, index) => {
      const cardFields = card.fields;
      if (!cardFields) return null;

      const linkValue = cardFields.link?.value || {};
      const title = cardFields.title?.value || `Featured content ${index + 1}`;
      const labelColor = cardFields.labelTone?.value || '#efd196';

      return {
        ...card,
        fields: cardFields,
        label: cardFields.label?.value || '',
        labelStyle: getLabelStyle(labelColor),
        href: linkValue.href || '',
        target: linkValue.target,
        linkText: cardFields.linkText?.value,
        linkLabel: linkValue.title || cardFields.linkText?.value || linkValue.text || title,
        showLink: Boolean(linkValue.href),
        hasMedia: Boolean(
          cardFields.image?.fields?.desktopImage?.value?.src ||
          cardFields.image?.fields?.mobileImage?.value?.src
        ),
      };
    })
    .filter((card): card is NormalizedCard => Boolean(card))
);

const getLabelStyle = (backgroundColor: string) => ({
  backgroundColor,
  color: shouldUseLightText(backgroundColor) ? '#fff' : '#454545',
});

const shouldUseLightText = (color: string) => {
  const hex = color.trim().replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return false;

  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  return (red * 299 + green * 587 + blue * 114) / 1000 < 145;
};

const updateActiveCard = () => {
  const track = mobileTrackEl.value;
  if (!track) return;

  const card = track.querySelector<HTMLElement>('.c-featured-content-cards__card');
  if (!card) return;

  const gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
  const step = card.offsetWidth + gap;
  activeCardIndex.value = Math.min(
    cards.value.length - 1,
    Math.max(0, Math.round(track.scrollLeft / step))
  );
};

const scrollToCard = (index: number) => {
  const track = mobileTrackEl.value;
  if (!track) return;

  const card = track.querySelector<HTMLElement>('.c-featured-content-cards__card');
  if (!card) return;

  const gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
  track.scrollTo({
    left: index * (card.offsetWidth + gap),
    behavior: 'smooth',
  });
};

const syncTitleHeight = () => {
  const grid = mobileTrackEl.value;
  if (!grid || window.innerWidth < 744) {
    titleHeight.value = 0;
    return;
  }

  titleHeight.value = Math.ceil(
    Math.max(
      0,
      ...Array.from(grid.querySelectorAll<HTMLElement>('.c-featured-content-cards__title')).map(
        (title) => title.getBoundingClientRect().height
      )
    )
  );
};

onMounted(async () => {
  await nextTick();
  syncTitleHeight();

  if (window.ResizeObserver && mobileTrackEl.value) {
    titleResizeObserver = new ResizeObserver(syncTitleHeight);
    titleResizeObserver.observe(mobileTrackEl.value);
  }
});

watch(
  () => cards.value.length,
  () => nextTick(syncTitleHeight)
);

onBeforeUnmount(() => {
  titleResizeObserver?.disconnect();
});

return (_ctx: any,_cache: any) => {
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value && cards.value.length)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: __props.rendering?.dataSource
      }, [
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [2, 20],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            (fields.value?.title?.value || fields.value?.description?.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  (fields.value?.title?.value)
                    ? (_openBlock(), _createElementBlock("h2", {
                        key: 0,
                        class: "c-featured-content-cards__heading",
                        innerHTML: fields.value.title.value
                      }, null, 8, _hoisted_3))
                    : _createCommentVNode("", true),
                  (fields.value?.description?.value)
                    ? (_openBlock(), _createBlock(_unref(RichText), {
                        key: 1,
                        class: "c-featured-content-cards__intro",
                        field: fields.value.description
                      }, null, 8, ["field"]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              ref_key: "mobileTrackEl",
              ref: mobileTrackEl,
              class: "c-featured-content-cards__grid",
              onScroll: updateActiveCard
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cards.value, (card) => {
                return (_openBlock(), _createElementBlock("article", {
                  key: card.id,
                  class: "c-featured-content-cards__card"
                }, [
                  (card.hasMedia)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(card.href ? 'a' : 'div'), {
                        key: 0,
                        class: "c-featured-content-cards__media-link",
                        href: card.href || undefined,
                        target: "_blank",
                        "aria-label": card.linkLabel
                      }, {
                        default: _withCtx(() => [
                          (card.label)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 0,
                                class: "c-featured-content-cards__label",
                                style: _normalizeStyle(card.labelStyle)
                              }, _toDisplayString(card.label), 5))
                            : _createCommentVNode("", true),
                          (card.fields.image)
                            ? (_openBlock(), _createBlock(AdaptiveImage, {
                                key: 1,
                                class: "c-featured-content-cards__image",
                                field: card.fields.image,
                                show: true,
                                "enable-scroll-detection": false,
                                "show-image-redius": false
                              }, null, 8, ["field"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1032, ["href", "aria-label"]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_4, [
                    (card.fields.title?.value)
                      ? (_openBlock(), _createBlock(_unref(RichText), {
                          key: 0,
                          class: "c-featured-content-cards__title",
                          field: card.fields.title,
                          tag: "h3"
                        }, null, 8, ["field"]))
                      : _createCommentVNode("", true),
                    (card.fields.description?.value)
                      ? (_openBlock(), _createBlock(_unref(RichText), {
                          key: 1,
                          class: "c-featured-content-cards__description",
                          field: card.fields.description
                        }, null, 8, ["field"]))
                      : _createCommentVNode("", true),
                    (card.linkText)
                      ? (_openBlock(), _createElementBlock("a", {
                          key: 2,
                          class: "c-featured-content-cards__link",
                          href: card.href,
                          target: "_blank"
                        }, [
                          _createElementVNode("span", null, _toDisplayString(card.linkText), 1),
                          _cache[0] || (_cache[0] = _createElementVNode("span", {
                            class: "c-featured-content-cards__arrow",
                            "aria-hidden": "true"
                          }, null, -1))
                        ], 8, _hoisted_5))
                      : _createCommentVNode("", true)
                  ])
                ]))
              }), 128))
            ], 544),
            (cards.value.length > 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cards.value, (_, index) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: `featured-content-card-pagination-${index}`,
                      type: "button",
                      class: _normalizeClass(["c-featured-content-cards__pagination-item", { 'is-active': activeCardIndex.value === index }]),
                      tabindex: "-1",
                      onClick: ($event: any) => (scrollToCard(index))
                    }, null, 10, _hoisted_7))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})